'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useLabsStore, usePack } from '../pack-context';
import { isMetricCorrect } from '../scoring';
import type { LabsDerivedMetric, LabsUnit } from '../types';
import { useInstantMotion } from '../use-instant-motion';
import { PillTag, StepNav } from './_components';
import { StepShell } from './step-shell';

/**
 * M3 · Activity — the practice subject: read its table, compute the derived metrics off it, diagnose
 * it in a sentence. Styled to the SOURCE vanilla prototype (`data-view="m3b"` + `.statement-table` /
 * `.ratio-row-mc` in styles.css), rendered through the Extern finance-sprint design system (fs-*
 * tokens + DM Serif Display / Inter). STATIC for everyone; every number comes from the pack — none is
 * hardcoded in JSX.
 *
 * LAYOUT (from the HTML prototype, NOT the prior Figma cards):
 *   1. A LEFT-aligned header: uppercase eyebrow, the practice subject's serif name, a muted subhead.
 *   2. The subject's table inside a white rounded card with a `.stmt-title` strip on top (subject name
 *      + the amber "practice example" tag). A header row (empty first cell, then the period columns
 *      with the unit, right-aligned, uppercase muted), then the rows in order; SUBTOTAL rows are bold
 *      on a subtle fill; negative values render danger-red. Values are tabular-nums, right-aligned.
 *   3. The metric picker (`.ratio-row-mc`) — one white card holding hairline-separated rows. Each row
 *      is a LABEL BLOCK on the left (serif metric name + period, mono formula, a feedback line) and
 *      the answer CHOICES on the right (pill buttons). Picking grades via `isMetricCorrect` against
 *      the pack's tolerance: correct → green chip + the pack's "correct" line; wrong → light-red chip
 *      + the "wrong" line (and the row stays selectable). Each correct pick records to the store.
 *      Engaging a row (hover/focus-within) boxes the table cells that metric is computed from.
 *   4. Diagnosis (`#m3-reveal`) — a white card: heading, a textarea, then a [char-counter · "Reveal the
 *      answer" text-link] row; the link auto-fills the pack's sample if empty and opens the reveal.
 *
 * THREE FINANCE ASSUMPTIONS LIVED IN THIS SCREEN'S STRUCTURE, and are now data:
 *   • the row LABELS were a hardcoded list in the view, ordered to match a `Record` of values — so the
 *     table could only ever be an income statement. The subject now ships ORDERED, self-describing
 *     rows and this screen just iterates them.
 *   • the subtotal rows were `new Set([2, 4])` — the INDEXES of Gross Profit and Operating Income. A
 *     row now declares its own `emphasis`.
 *   • the cell format was `` `$${v}${magnitude}` ``. It is now the subject's `unit`, sign-first so a
 *     negative reads "-$0.4B" rather than "$-0.4B" (the practice subject has no negatives, but the
 *     red-negative rule is carried faithfully and the real M4 subjects do).
 *
 * Token mapping (SOURCE intent → fs-* token):
 *   - source eyebrow #6B7280                      → fs-text-muted (uppercase, tracked)
 *   - source card #FFFFFF + #E6E4DD               → fs-surface + fs-border, rounded-fs-card
 *   - source `.stmt-title` / table header muted   → fs-text-muted
 *   - source `.subtotal` bold + #FAFAF7 fill      → fs-text (medium) + fs-surface-subtle
 *   - source `.cell-highlight` brand-green pill   → fs-brand-subtle fill + fs-brand text + fs-focus ring (the v2 brand analog)
 *   - source practice tag (accent-soft amber)     → PillTag warning (fs-warning-subtle / fs-warning)
 *   - source selected pill brand-green            → fs-primary fill / fs-on-primary
 *   - source `.ok` green / `.fail` light-red      → fs-success(+subtle) / fs-danger(+subtle)
 *   - source primary CTA brand-green              → BrandButton primary (fs-primary, the v2 brand)
 */

/** The diagnosis box's hard cap (source `#m3-reveal` textarea) — one sentence, not an essay. */
const DIAGNOSIS_MAX_CHARS = 500;

/** LCG period (2^31) — the modulus for the seeded deterministic shuffle RNG below. */
const LCG_MODULUS = 2_147_483_648;

/** A subject cell, e.g. "$55M" / "-$0.4B" — sign first, so the minus never lands inside the unit. */
function formatCell(unit: LabsUnit, value: number): string {
  const sign = value < 0 ? '-' : '';
  return `${sign}${unit.prefix}${Math.abs(value)}${unit.suffix}`;
}

/** The unit shown in a period column header, e.g. `($M)` / `(€B)`. */
function unitLabel(unit: LabsUnit): string {
  return `(${unit.prefix}${unit.suffix})`;
}

/**
 * Deterministic 0..1 RNG (linear-congruential, glibc constants, mod 2^31) used ONLY under automation
 * so the per-visit option shuffle is stable for visual-regression baselines. Pure arithmetic (no
 * bitwise / no assignment-in-expression) — real users get `Math.random` instead.
 */
function makeSeededRng(initialSeed: number): () => number {
  let seed = initialSeed % LCG_MODULUS;
  return () => {
    seed = (seed * 1_103_515_245 + 12_345) % LCG_MODULUS;
    return seed / LCG_MODULUS;
  };
}

/**
 * A per-visit shuffled copy of a metric's options (Fisher–Yates over a seedable RNG), so the correct
 * answer isn't always in the same slot.
 */
function shuffleOptions(options: readonly number[], rng: () => number): number[] {
  const out = [...options];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = out[i] as number;
    out[i] = out[j] as number;
    out[j] = tmp;
  }
  return out;
}

/** Answer-pill state (correct → green / wrong → light red / unpicked → neutral) — avoids a nested ternary. */
function choiceClass(selected: boolean, correct: boolean): string {
  if (selected && correct) {
    return 'border-fs-success bg-fs-success-subtle text-fs-success';
  }
  if (selected) {
    return 'border-fs-danger bg-fs-danger-subtle text-fs-danger';
  }
  return 'border-fs-border text-fs-text hover:border-fs-primary hover:bg-fs-canvas';
}
export function M3Activity() {
  const $ = _c(116);
  const {
    copy,
    m3
  } = usePack();
  const {
    metrics,
    subject,
    tolerance,
    unitSuffix
  } = m3;
  const activityCopy = copy.m3.activity;
  const next = useLabsStore(_temp);
  const setM3Answer = useLabsStore(_temp2);
  const w = typeof window === "undefined" ? undefined : window as unknown as {
    __labsNoAuto?: boolean;
  };
  const automated = w?.__labsNoAuto || typeof navigator !== "undefined" && navigator.webdriver;
  let t0;
  if ($[0] !== metrics) {
    const rng = automated ? makeSeededRng(1597463007) : Math.random;
    t0 = Object.fromEntries(metrics.map(metric => [metric.key, shuffleOptions(metric.options, rng)]));
    $[0] = metrics;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const shuffled = t0 as Record<string, number[]>;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [picks, setPicks] = useState(t1);
  const [diagnosis, setDiagnosis] = useState("");
  const [revealed, setRevealed] = useState(false);
  const instant = useInstantMotion();
  const [lastPickedKey, setLastPickedKey] = useState(null);
  const [hoverMetricKey, setHoverMetricKey] = useState(null);
  const highlightKey = instant ? metrics[0]?.key : hoverMetricKey ?? lastPickedKey;
  let t2;
  if ($[3] !== highlightKey || $[4] !== metrics) {
    let t3;
    if ($[6] !== highlightKey) {
      t3 = m => m.key === highlightKey;
      $[6] = highlightKey;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const metric_0 = metrics.find(t3);
    t2 = new Set(metric_0?.sources.map(_temp3) ?? []);
    $[3] = highlightKey;
    $[4] = metrics;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const highlightedCells = t2;
  let t3;
  if ($[8] !== setM3Answer || $[9] !== tolerance) {
    t3 = (metric_1, value) => {
      setPicks(p => ({
        ...p,
        [metric_1.key]: value
      }));
      setLastPickedKey(metric_1.key);
      if (isMetricCorrect(metric_1, value, tolerance)) {
        setM3Answer(metric_1.key, String(value));
      }
    };
    $[8] = setM3Answer;
    $[9] = tolerance;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handlePick = t3;
  let t4;
  if ($[11] !== activityCopy.diagnosisSample || $[12] !== diagnosis) {
    t4 = () => {
      if (!diagnosis.trim()) {
        setDiagnosis(activityCopy.diagnosisSample);
      }
      setRevealed(true);
    };
    $[11] = activityCopy.diagnosisSample;
    $[12] = diagnosis;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleReveal = t4;
  let t5;
  if ($[14] !== activityCopy.eyebrow) {
    t5 = <div className="font-medium text-[11px] text-fs-text-muted uppercase tracking-[0.12em]">{activityCopy.eyebrow}</div>;
    $[14] = activityCopy.eyebrow;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== subject.name) {
    t6 = <h1 className="font-instrument text-fs-display-sm text-fs-text sm:text-fs-display-md">{subject.name}</h1>;
    $[16] = subject.name;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== activityCopy.subhead) {
    t7 = <p className="text-fs-body-lg text-fs-text-subtle">{activityCopy.subhead}</p>;
    $[18] = activityCopy.subhead;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = <header className="flex flex-col gap-2">{t5}{t6}{t7}</header>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== subject.name) {
    t9 = <span className="font-medium text-fs-body-lg text-fs-text">{subject.name}</span>;
    $[24] = subject.name;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== activityCopy.tableTag) {
    t10 = <PillTag variant="warning">{activityCopy.tableTag}</PillTag>;
    $[26] = activityCopy.tableTag;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t9) {
    t11 = <div className="flex items-center justify-between gap-2 border-fs-border border-b px-4 py-4">{t9}{t10}</div>;
    $[28] = t10;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="flex-1" />;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== subject.periods || $[33] !== subject.unit) {
    let t14;
    if ($[35] !== subject.unit) {
      t14 = period => <span className="flex-1 text-right" key={period}>{period} {unitLabel(subject.unit)}</span>;
      $[35] = subject.unit;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    t13 = subject.periods.map(t14);
    $[32] = subject.periods;
    $[33] = subject.unit;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[37] !== t13) {
    t14 = <div className="flex items-center px-4 py-3 text-fs-label-md text-fs-text-muted uppercase tracking-[0.06em]">{t12}<div className="flex w-[320px] shrink-0">{t13}</div></div>;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== highlightedCells || $[40] !== instant || $[41] !== subject.periods || $[42] !== subject.rows || $[43] !== subject.unit) {
    let t16;
    if ($[45] !== highlightedCells || $[46] !== instant || $[47] !== subject.periods || $[48] !== subject.unit) {
      t16 = row => {
        const isSubtotal = row.emphasis === "subtotal";
        return <div className={cn("flex items-center border-fs-border border-t px-4 py-3 text-fs-body-md", isSubtotal && "bg-fs-surface-subtle")} key={row.key}><span className={cn("flex-1 text-fs-text", isSubtotal && "font-medium")}>{row.label}</span><div className="flex w-[320px] shrink-0">{row.values.map((v, periodIndex) => {
              const highlighted = highlightedCells.has(`${row.key}:${periodIndex}`);
              const negative = v < 0;
              return <span className="flex flex-1 justify-end" key={`${row.key}:${subject.periods[periodIndex]}`}><span className={cn("rounded-fs-button px-2 py-0.5 text-right tabular-nums", isSubtotal ? "font-medium text-fs-text" : "text-fs-text", negative && "text-fs-danger", !instant && "transition-[color,background-color,box-shadow] duration-200 ease-out", highlighted && "bg-fs-brand-subtle text-fs-brand ring-1 ring-fs-focus")}>{formatCell(subject.unit, v)}</span></span>;
            })}</div></div>;
      };
      $[45] = highlightedCells;
      $[46] = instant;
      $[47] = subject.periods;
      $[48] = subject.unit;
      $[49] = t16;
    } else {
      t16 = $[49];
    }
    t15 = subject.rows.map(t16);
    $[39] = highlightedCells;
    $[40] = instant;
    $[41] = subject.periods;
    $[42] = subject.rows;
    $[43] = subject.unit;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[50] !== t11 || $[51] !== t14 || $[52] !== t15) {
    t16 = <div className="w-full overflow-hidden rounded-fs-card border border-fs-border bg-fs-surface"><div className="overflow-x-auto"><div className="min-w-[460px]">{t11}{t14}{t15}</div></div></div>;
    $[50] = t11;
    $[51] = t14;
    $[52] = t15;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== activityCopy.metricsHeading) {
    t17 = <h2 className="text-fs-label-md text-fs-text-muted uppercase tracking-[0.06em]">{activityCopy.metricsHeading}</h2>;
    $[54] = activityCopy.metricsHeading;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== activityCopy.metricsHelper) {
    t18 = <p className="text-fs-label-md text-fs-text-muted">{activityCopy.metricsHelper}</p>;
    $[56] = activityCopy.metricsHelper;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== activityCopy.feedbackCorrect || $[59] !== activityCopy.feedbackWrong || $[60] !== handlePick || $[61] !== instant || $[62] !== metrics || $[63] !== picks || $[64] !== shuffled || $[65] !== tolerance || $[66] !== unitSuffix) {
    let t20;
    if ($[68] !== activityCopy.feedbackCorrect || $[69] !== activityCopy.feedbackWrong || $[70] !== handlePick || $[71] !== instant || $[72] !== picks || $[73] !== shuffled || $[74] !== tolerance || $[75] !== unitSuffix) {
      t20 = (metric_2, i) => {
        const pick = picks[metric_2.key];
        const pickIsCorrect = pick !== undefined && isMetricCorrect(metric_2, pick, tolerance);
        return <div className={cn("flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between", i > 0 && "border-fs-border border-t")} key={metric_2.key} onBlur={instant ? undefined : e => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setHoverMetricKey(null);
          }
        }} onFocus={instant ? undefined : () => setHoverMetricKey(metric_2.key)} onMouseEnter={instant ? undefined : () => setHoverMetricKey(metric_2.key)} onMouseLeave={instant ? undefined : () => setHoverMetricKey(null)}><div className="flex min-w-0 flex-col gap-2"><span className="font-instrument text-fs-text text-fs-title-lg leading-tight">{metric_2.name} <span className="whitespace-nowrap">{metric_2.period}</span></span><span className="font-mono text-fs-body-md text-fs-text-subtle tabular-nums">{metric_2.formula}</span>{pick === undefined ? null : <span className={cn("font-medium text-fs-label-md", pickIsCorrect ? "text-fs-success" : "text-fs-danger")}>{pickIsCorrect ? activityCopy.feedbackCorrect : activityCopy.feedbackWrong}</span>}</div><div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">{shuffled[metric_2.key]?.map(opt => {
              const selected = pick === opt;
              const selectedCorrect = selected && isMetricCorrect(metric_2, opt, tolerance);
              return <button aria-pressed={selected} className={cn("min-w-[84px] cursor-pointer rounded-fs-pill border px-4 py-2 font-mono font-semibold text-fs-body-lg tabular-nums transition-colors", choiceClass(selected, selectedCorrect))} key={opt} onClick={() => handlePick(metric_2, opt)} type="button">{`${opt.toFixed(1)}${unitSuffix}`}</button>;
            })}</div></div>;
      };
      $[68] = activityCopy.feedbackCorrect;
      $[69] = activityCopy.feedbackWrong;
      $[70] = handlePick;
      $[71] = instant;
      $[72] = picks;
      $[73] = shuffled;
      $[74] = tolerance;
      $[75] = unitSuffix;
      $[76] = t20;
    } else {
      t20 = $[76];
    }
    t19 = metrics.map(t20);
    $[58] = activityCopy.feedbackCorrect;
    $[59] = activityCopy.feedbackWrong;
    $[60] = handlePick;
    $[61] = instant;
    $[62] = metrics;
    $[63] = picks;
    $[64] = shuffled;
    $[65] = tolerance;
    $[66] = unitSuffix;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[77] !== t19) {
    t20 = <div className="mt-4 flex flex-col">{t19}</div>;
    $[77] = t19;
    $[78] = t20;
  } else {
    t20 = $[78];
  }
  let t21;
  if ($[79] !== t17 || $[80] !== t18 || $[81] !== t20) {
    t21 = <div className="flex flex-col gap-1 rounded-fs-card border border-fs-border bg-fs-surface p-6">{t17}{t18}{t20}</div>;
    $[79] = t17;
    $[80] = t18;
    $[81] = t20;
    $[82] = t21;
  } else {
    t21 = $[82];
  }
  let t22;
  let t23;
  if ($[83] !== activityCopy.diagnosisHeading) {
    t22 = <h2 className="text-fs-label-md text-fs-text-muted uppercase tracking-[0.06em]">{activityCopy.diagnosisHeading}</h2>;
    t23 = <label className="sr-only" htmlFor="m3-diagnosis">{activityCopy.diagnosisHeading}</label>;
    $[83] = activityCopy.diagnosisHeading;
    $[84] = t22;
    $[85] = t23;
  } else {
    t22 = $[84];
    t23 = $[85];
  }
  let t24;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e_0 => setDiagnosis(e_0.target.value);
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== activityCopy.diagnosisPlaceholder || $[88] !== diagnosis) {
    t25 = <textarea className="w-full resize-y rounded-fs-card border border-fs-border bg-fs-surface px-4 py-3 text-fs-body-lg text-fs-text placeholder:text-fs-text-muted focus:border-fs-focus focus:outline-none focus:ring-1 focus:ring-fs-focus" id="m3-diagnosis" maxLength={DIAGNOSIS_MAX_CHARS} onChange={t24} placeholder={activityCopy.diagnosisPlaceholder} rows={3} value={diagnosis} />;
    $[87] = activityCopy.diagnosisPlaceholder;
    $[88] = diagnosis;
    $[89] = t25;
  } else {
    t25 = $[89];
  }
  let t26;
  if ($[90] !== diagnosis.length) {
    t26 = <span>{diagnosis.length} characters</span>;
    $[90] = diagnosis.length;
    $[91] = t26;
  } else {
    t26 = $[91];
  }
  let t27;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Eye className="h-3.5 w-3.5" strokeWidth={1.75} />;
    $[92] = t27;
  } else {
    t27 = $[92];
  }
  let t28;
  if ($[93] !== handleReveal) {
    t28 = <button className="inline-flex cursor-pointer items-center gap-fs-xxs font-medium text-fs-brand underline-offset-4 transition-colors hover:underline" onClick={handleReveal} type="button">{t27}Reveal the answer</button>;
    $[93] = handleReveal;
    $[94] = t28;
  } else {
    t28 = $[94];
  }
  let t29;
  if ($[95] !== t26 || $[96] !== t28) {
    t29 = <div className="flex items-center justify-between gap-4 text-fs-label-md text-fs-text-muted">{t26}{t28}</div>;
    $[95] = t26;
    $[96] = t28;
    $[97] = t29;
  } else {
    t29 = $[97];
  }
  let t30;
  if ($[98] !== activityCopy.revealBody || $[99] !== activityCopy.revealLabel || $[100] !== revealed) {
    t30 = revealed ? <div className="rounded-fs-card border border-fs-brand-subtle bg-fs-brand-subtle p-5 text-fs-body-md text-fs-text-subtle leading-relaxed"><span className="font-medium text-fs-brand">{activityCopy.revealLabel}</span> {activityCopy.revealBody}</div> : null;
    $[98] = activityCopy.revealBody;
    $[99] = activityCopy.revealLabel;
    $[100] = revealed;
    $[101] = t30;
  } else {
    t30 = $[101];
  }
  let t31;
  if ($[102] !== t22 || $[103] !== t23 || $[104] !== t25 || $[105] !== t29 || $[106] !== t30) {
    t31 = <div className="flex flex-col gap-3 rounded-fs-card border border-fs-border bg-fs-surface p-6">{t22}{t23}{t25}{t29}{t30}</div>;
    $[102] = t22;
    $[103] = t23;
    $[104] = t25;
    $[105] = t29;
    $[106] = t30;
    $[107] = t31;
  } else {
    t31 = $[107];
  }
  let t32;
  if ($[108] !== next) {
    t32 = <StepNav onNext={next} />;
    $[108] = next;
    $[109] = t32;
  } else {
    t32 = $[109];
  }
  let t33;
  if ($[110] !== t16 || $[111] !== t21 || $[112] !== t31 || $[113] !== t32 || $[114] !== t8) {
    t33 = <StepShell><div className="flex w-full flex-col gap-8">{t8}{t16}{t21}{t31}{t32}</div></StepShell>;
    $[110] = t16;
    $[111] = t21;
    $[112] = t31;
    $[113] = t32;
    $[114] = t8;
    $[115] = t33;
  } else {
    t33 = $[115];
  }
  return t33;
}
function _temp3(cell) {
  return `${cell.row}:${cell.periodIndex}`;
}
function _temp2(s_0) {
  return s_0.setM3Answer;
}
function _temp(s) {
  return s.next;
}